import { useAccess } from "@/auth/AccessProvider";
import { useAuth } from "@/auth/AuthProvider";
import { exploreRouteForDepartment } from "@/analytics/dashboards/registry";
import { listDepartments, getDepartmentGridLabel } from "@/grid/config/registry";
import { teamRouteForName } from "@/team-dashboard/lib/team-slug";
import { useAccessibleTeams } from "@/team-dashboard/hooks/useAccessibleTeams";
import { cn } from "@/lib/cn";
import { Check, Menu, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

interface AppMenuProps {
  className?: string;
  /** Give the trigger its own background/shadow so it stays legible over scrolling content. */
  floating?: boolean;
}

function NavSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-[var(--color-border)] pt-4 first:border-t-0 first:pt-0">
      <p className="mb-2 px-3 text-[11px] font-semibold tracking-wide text-[var(--color-muted-foreground)]/55">
        {title}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export function AppMenu({ className, floating = false }: AppMenuProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { signOut } = useAuth();
  const { canAccess, isAdmin } = useAccess();
  const departments = listDepartments();
  const { data: accessibleTeams } = useAccessibleTeams();
  const showTeamDashboards = canAccess("team.dashboard") && (accessibleTeams?.length ?? 0) > 0;

  const close = () => setOpen(false);

  const navLinkClass = (isActive: boolean) =>
    cn(
      "flex items-center justify-between rounded-lg px-3 py-2.5 text-[15px] leading-snug",
      isActive
        ? "bg-[var(--color-muted)] font-medium text-[var(--color-foreground)]"
        : "text-[var(--color-foreground)]/75 hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]",
    );

  const showExplore = departments.some((department) =>
    canAccess(department.id === "sdr" ? "explore.sdr" : "explore.sales"),
  );

  const showConfig = canAccess("targets") || canAccess("audit") || canAccess("raw") || isAdmin;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center justify-center rounded-full",
          "text-[var(--color-muted-foreground)] transition-colors",
          "hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]",
          floating &&
            "bg-[var(--color-background)]/90 shadow-[0_2px_12px_rgba(0,0,0,0.08)] backdrop-blur-sm",
          className ?? "h-10 w-10",
        )}
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {open ? (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/20 backdrop-blur-[1px]"
            aria-label="Close menu"
            onClick={close}
          />

          <aside
            className={cn(
              "absolute right-0 top-0 flex h-full w-[min(100%,280px)] flex-col",
              "border-l border-[var(--color-border)] bg-[var(--color-background)] shadow-xl",
              "standalone-safe-top standalone-safe-bottom",
            )}
          >
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-4">
              <p className="text-sm font-semibold text-[var(--color-foreground)]">Menu</p>
              <button
                type="button"
                onClick={close}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--color-muted)]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 space-y-4 overflow-y-auto p-3">
              <NavSection title="Dashboards">
                {canAccess("company") ? (
                  <Link
                    to="/company/sales"
                    onClick={close}
                    className={navLinkClass(location.pathname.startsWith("/company"))}
                  >
                    <span>Company Sales Overview</span>
                    {location.pathname.startsWith("/company") ? (
                      <Check className="h-4 w-4" />
                    ) : null}
                  </Link>
                ) : null}
                {departments.map((department) => {
                  const routeKey = department.id === "sdr" ? "grid.sdr" : "grid.sales";
                  if (!canAccess(routeKey)) return null;
                  const href = `/grid/${department.id}`;
                  const isActive = location.pathname === href;
                  return (
                    <Link
                      key={department.id}
                      to={href}
                      onClick={close}
                      className={navLinkClass(isActive)}
                    >
                      <span>{getDepartmentGridLabel(department)}</span>
                      {isActive ? <Check className="h-4 w-4" /> : null}
                    </Link>
                  );
                })}
                {showExplore
                  ? departments.map((department) => {
                      const routeKey = department.id === "sdr" ? "explore.sdr" : "explore.sales";
                      if (!canAccess(routeKey)) return null;
                      const href = exploreRouteForDepartment(department.id);
                      const isActive = location.pathname === href;
                      return (
                        <Link
                          key={`explore-${department.id}`}
                          to={href}
                          onClick={close}
                          className={navLinkClass(isActive)}
                        >
                          <span>{department.name} Explore</span>
                          {isActive ? <Check className="h-4 w-4" /> : null}
                        </Link>
                      );
                    })
                  : null}
              </NavSection>

              {showTeamDashboards ? (
                <NavSection title="Team Dashboards">
                  {(accessibleTeams ?? []).map((team) => {
                    const href = teamRouteForName(team.teamName);
                    const isActive = location.pathname === href;
                    return (
                      <Link
                        key={team.teamGroupKey}
                        to={href}
                        onClick={close}
                        className={navLinkClass(isActive)}
                      >
                        <span>{team.teamName}</span>
                        {isActive ? <Check className="h-4 w-4" /> : null}
                      </Link>
                    );
                  })}
                </NavSection>
              ) : null}

              {showConfig ? (
                <NavSection title="Config">
                  {canAccess("targets") ? (
                    <Link
                      to="/targets/sales"
                      onClick={close}
                      className={navLinkClass(location.pathname.startsWith("/targets"))}
                    >
                      <span>Targets</span>
                      {location.pathname.startsWith("/targets") ? (
                        <Check className="h-4 w-4" />
                      ) : null}
                    </Link>
                  ) : null}
                  {canAccess("audit") ? (
                    <Link
                      to="/audit"
                      onClick={close}
                      className={navLinkClass(location.pathname.startsWith("/audit"))}
                    >
                      <span>Data audit</span>
                      {location.pathname.startsWith("/audit") ? (
                        <Check className="h-4 w-4" />
                      ) : null}
                    </Link>
                  ) : null}
                  {canAccess("raw") ? (
                    <Link
                      to="/raw/sync-runs"
                      onClick={close}
                      className={navLinkClass(location.pathname.startsWith("/raw"))}
                    >
                      <span>Raw data</span>
                      {location.pathname.startsWith("/raw") ? <Check className="h-4 w-4" /> : null}
                    </Link>
                  ) : null}
                  {isAdmin ? (
                    <Link
                      to="/admin/invites"
                      onClick={close}
                      className={navLinkClass(location.pathname.startsWith("/admin/invites"))}
                    >
                      <span>Invite codes</span>
                      {location.pathname.startsWith("/admin/invites") ? (
                        <Check className="h-4 w-4" />
                      ) : null}
                    </Link>
                  ) : null}
                </NavSection>
              ) : null}
            </nav>

            <div className="space-y-2 border-t border-[var(--color-border)] p-4">
              <Link
                to="/account/password"
                onClick={close}
                className="block w-full rounded-lg border border-[var(--color-border)] px-3 py-2.5 text-center text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
              >
                Reset password
              </Link>
              <button
                type="button"
                onClick={() => {
                  close();
                  void signOut();
                }}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
              >
                Sign out
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
}
